/**
 * 全角数字を半角数字に自動変換するユーティリティ
 *
 * @description
 * 半角数字のみを受け付けるinputに全角数字が入力された場合、
 * 自動的に半角数字に変換するための関数群を提供します。
 *
 * 主な使用方法:
 * - Form.Item の normalize プロパティ
 * - InputNumber の parser プロパティ
 * - Input の onChange ハンドラー
 */

/**
 * 全角数字を半角数字に変換する
 *
 * @param value - 変換対象の値（文字列または数値）
 * @returns 半角数字に変換された文字列
 *
 * @example
 * toHalfWidthNumber('０１２３') // '0123'
 * toHalfWidthNumber('１００円')  // '100円'
 * toHalfWidthNumber(123)        // '123'
 */
export const toHalfWidthNumber = (
  value: string | number | undefined | null
): string => {
  if (value === undefined || value === null) return '';
  const str = String(value);
  // 全角数字（U+FF10〜U+FF19）を半角数字（U+0030〜U+0039）に変換
  // 差分は0xFEE0
  return str.replace(/[０-９]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0)
  );
};

/**
 * Form.Item 用の normalize 関数（数値型）
 *
 * @description
 * Form.Item の normalize プロパティで使用し、
 * 全角数字を半角に変換した後、数値として返します。
 *
 * 注意: InputNumber の場合は、この関数ではなく parseInputNumber を
 * InputNumber の parser プロパティに直接設定してください。
 *
 * @param value - フォームから受け取る値
 * @returns 変換後の数値、または undefined
 *
 * @example
 * // 通常のInputで数値を扱う場合（InputNumberではない場合）
 * <Form.Item name="quantity" normalize={normalizeToNumber}>
 *   <Input type="number" />
 * </Form.Item>
 */
export const normalizeToNumber = (
  value: string | number | undefined | null
): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  // 数値型の場合はそのまま返す
  if (typeof value === 'number') {
    return value;
  }

  // 全角数字を半角に変換
  const halfWidth = toHalfWidthNumber(value);

  // 数値に変換（カンマ区切りにも対応）
  const cleaned = halfWidth.replace(/,/g, '');
  const num = parseFloat(cleaned);

  return isNaN(num) ? undefined : num;
};

/**
 * Form.Item 用の normalize 関数（整数型）
 *
 * @description
 * 小数点を含まない整数のみを扱う場合に使用します。
 *
 * 注意: InputNumber の場合は、この関数ではなく parseInputNumber を
 * InputNumber の parser プロパティに直接設定してください。
 *
 * @param value - フォームから受け取る値
 * @returns 変換後の整数、または undefined
 *
 * @example
 * // 通常のInputで整数を扱う場合（InputNumberではない場合）
 * <Form.Item name="quantity" normalize={normalizeToInteger}>
 *   <Input type="number" />
 * </Form.Item>
 */
export const normalizeToInteger = (
  value: string | number | undefined | null
): number | undefined => {
  const num = normalizeToNumber(value);
  if (num === undefined) return undefined;
  return Math.floor(num);
};

/**
 * Form.Item 用の normalize 関数（文字列型、数字のみ）
 *
 * @description
 * 通常の Input で数字のみを受け付ける場合に使用します。
 * 電話番号、郵便番号など、先頭の0を保持したい場合に適しています。
 *
 * @param value - フォームから受け取る値
 * @returns 数字のみの文字列
 *
 * @example
 * <Form.Item name="phone" normalize={normalizeToDigitString}>
 *   <Input />
 * </Form.Item>
 */
export const normalizeToDigitString = (
  value: string | number | undefined | null
): string => {
  if (value === undefined || value === null) return '';

  // 全角数字を半角に変換
  const halfWidth = toHalfWidthNumber(value);

  // 数字以外を除去
  return halfWidth.replace(/[^0-9]/g, '');
};

/**
 * InputNumber 用の parser 関数
 *
 * @description
 * InputNumber の parser プロパティで使用し、
 * 全角数字を半角に変換します。
 * Ant Design InputNumber のフォーマット表示と組み合わせて使用できます。
 *
 * @param displayValue - InputNumber が表示している文字列
 * @returns パースされた数値、または空文字
 *
 * @example
 * <InputNumber
 *   parser={parseInputNumber}
 *   formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
 * />
 */
export const parseInputNumber = (
  displayValue: string | undefined
): number | '' => {
  if (!displayValue) return '';

  // 全角数字を半角に変換
  const halfWidth = toHalfWidthNumber(displayValue);

  // 数字、小数点、マイナス以外を除去
  const cleaned = halfWidth.replace(/[^\d.-]/g, '');
  const num = parseFloat(cleaned);

  return isNaN(num) ? '' : num;
};

// =============================================================================
// 電話番号・郵便番号・メールアドレス用の変換関数
// =============================================================================

/**
 * 全角文字を半角文字に変換する（数字・記号）
 *
 * @description
 * 全角の数字、ハイフン、カッコ、アットマーク、ドットなどを
 * 半角に変換します。
 *
 * @param value - 変換対象の文字列
 * @returns 半角に変換された文字列
 *
 * @example
 * toHalfWidth('０３−１２３４−５６７８') // '03-1234-5678'
 * toHalfWidth('ｔｅｓｔ＠ｅｘａｍｐｌｅ．ｃｏｍ') // 'test@example.com'
 */
export const toHalfWidth = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';

  return (
    value
      // 全角数字 → 半角数字
      .replace(/[０-９]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xfee0)
      )
      // 全角英字 → 半角英字
      .replace(/[Ａ-Ｚａ-ｚ]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xfee0)
      )
      // 全角ハイフン類 → 半角ハイフン
      .replace(/[ー－―‐−]/g, '-')
      // 全角カッコ → 半角カッコ
      .replace(/（/g, '(')
      .replace(/）/g, ')')
      // 全角アットマーク → 半角
      .replace(/＠/g, '@')
      // 全角ドット → 半角
      .replace(/．/g, '.')
      // 全角プラス → 半角
      .replace(/＋/g, '+')
      // 全角アンダースコア → 半角
      .replace(/＿/g, '_')
      // 全角スペース → 半角スペース
      .replace(/\u3000/g, ' ')
  );
};

/**
 * 電話番号を正規化する
 *
 * @description
 * 電話番号入力用。全角文字を半角に変換し、
 * 電話番号として有効な文字（数字、ハイフン、カッコ、プラス、スペース）のみを残します。
 *
 * @param value - 変換対象の電話番号
 * @returns 正規化された電話番号
 *
 * @example
 * normalizePhone('０３−１２３４−５６７８') // '03-1234-5678'
 * normalizePhone('（０９０）１２３４−５６７８') // '(090)1234-5678'
 */
export const normalizePhone = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';

  // 全角→半角変換
  const halfWidth = toHalfWidth(value);

  // 電話番号として有効な文字のみを残す（数字、ハイフン、カッコ、プラス、スペース）
  return halfWidth.replace(/[^0-9\-()+ ]/g, '');
};

/**
 * 郵便番号を正規化する
 *
 * @description
 * 郵便番号入力用。全角文字を半角に変換し、
 * 郵便番号として有効な文字（数字、ハイフン）のみを残します。
 *
 * @param value - 変換対象の郵便番号
 * @returns 正規化された郵便番号
 *
 * @example
 * normalizePostalCode('１２３−４５６７') // '123-4567'
 * normalizePostalCode('１２３４５６７') // '1234567'
 */
export const normalizePostalCode = (
  value: string | undefined | null
): string => {
  if (value === undefined || value === null) return '';

  // 全角→半角変換
  const halfWidth = toHalfWidth(value);

  // 郵便番号として有効な文字のみを残す（数字、ハイフン）
  return halfWidth.replace(/[^0-9-]/g, '');
};

/**
 * メールアドレスを正規化する
 *
 * @description
 * メールアドレス入力用。全角文字を半角に変換します。
 * メールアドレスとして有効な文字のみを残します。
 *
 * @param value - 変換対象のメールアドレス
 * @returns 正規化されたメールアドレス
 *
 * @example
 * normalizeEmail('ｔｅｓｔ＠ｅｘａｍｐｌｅ．ｃｏｍ') // 'test@example.com'
 * normalizeEmail('ＴＥＳＴ＠ＥＸＡＭＰＬＥ．ＣＯＭ') // 'TEST@EXAMPLE.COM'
 */
export const normalizeEmail = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';

  // 全角→半角変換
  return toHalfWidth(value);
};
